// Indicação de amigos (0.32): cada conta tem um código; quem cria a conta e usa o código ganha 10 dias
// com todas as abas liberadas. O servidor confere (uma vez por conta, nos primeiros 30 dias da conta).
// Fale conosco: problemas, erros e sugestões vão para a tabela "feedback" (você lê no painel do Supabase).
import { Linking, Platform, Share } from 'react-native';
import { supabase } from './supabase';
import { DEMO } from './env';
import { addDays, today } from './date';
import { setDoc } from '../store/store';
import { idioma, t } from './i18n';
import { versaoApp } from './atualizacoes';
import { semBloqueio } from '../auth/AuthContext';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { LOJA_ANDROID, LOJA_IOS, WEB_URL } from './config';

import { EMAIL_SUPORTE } from './termos';
export { EMAIL_SUPORTE };
const precisaSql = (e: any) => /function|relation|does not exist|schema cache|PGRST20/i.test(String(e?.message ?? e ?? ''));

export type MeuCodigo = { codigo: string; indicados: number } | { erro: 'servidor' | 'rede' };

export async function meuCodigo(): Promise<MeuCodigo> {
  if (DEMO) return { codigo: 'DEMO42', indicados: 2 };
  if (!supabase) return { erro: 'servidor' };
  const { data, error } = await supabase.rpc('meu_codigo_indicacao');
  if (error) return { erro: precisaSql(error) ? 'servidor' : 'rede' };
  return data as { codigo: string; indicados: number };
}

/** Usa o código de quem indicou. Devolve a mensagem para mostrar e, se deu certo, libera as abas aqui. */
export async function usarCodigo(codigo: string): Promise<{ ok: boolean; msg: string }> {
  const c = codigo.trim().toUpperCase();
  if (!/^[A-Z0-9]{4,10}$/.test(c)) return { ok: false, msg: t('Confira o código. Ele tem 6 letras e números.') };
  let r: { ok: boolean; erro?: string; ate?: string };
  if (DEMO) r = { ok: true, ate: addDays(today(), 9) };
  else {
    if (!supabase) return { ok: false, msg: t('Servidor não configurado.') };
    const { data, error } = await supabase.rpc('usar_indicacao', { p_codigo: c });
    if (error) return { ok: false, msg: precisaSql(error) ? t('O servidor ainda não está preparado para indicações. Tente de novo mais tarde.') : t('Sem internet. Tente de novo.') };
    r = data;
  }
  if (r.ate && (r.ok || r.erro === 'ja_usou')) setDoc('ajustes', d => ({ ...d, bonusAte: r.ate, indicadoPor: c }));
  if (r.ok) return { ok: true, msg: t('Pronto! Todas as abas estão liberadas até {data}.', { data: r.ate!.split('-').reverse().join('/') }) };
  const msg = r.erro === 'proprio' ? t('Esse código é o seu. Use o código de quem indicou o app para você.')
    : r.erro === 'ja_usou' ? t('Você já usou um código de indicação nesta conta.')
    : r.erro === 'antiga' ? t('O código de indicação vale só nos primeiros 30 dias da conta.')
    : t('Código não encontrado. Confira as letras e os números.');
  return { ok: false, msg };
}

/** Link do convite: abre a página com o botão de baixar a versão mais recente do app (Android) e o site. */
export const linkIndicacao = (codigo: string) => `${WEB_URL}/baixar?i=${encodeURIComponent(codigo)}`;

/** Os links que vão em todo convite: as lojas (Android e iPhone) e a página para o computador (0.65). */
export const textoLinks = (codigo: string) => t('Baixe o My Planner na loja: Android {android} · iPhone {iphone}', { android: LOJA_ANDROID, iphone: LOJA_IOS })
  + '\n' + t('No computador: {link}', { link: linkIndicacao(codigo) });

/** Convite pelo WhatsApp (abre o WhatsApp com o texto pronto para escolher o contato). */
export function convidarWhatsApp(codigo: string) {
  const txt = t('Vamos conversar pelo My Planner? Me adicione como amigo com o código {codigo} (aba Mensagens > Amigos).', { codigo })
    + '\n\n' + textoLinks(codigo);
  return semBloqueio(() => Linking.openURL('https://wa.me/?text=' + encodeURIComponent(txt))).catch(() => Share.share({ message: txt }).catch(() => {}));
}

export function compartilharCodigo(codigo: string, viaWhats = false) {
  const msg = t('Estou usando o My Planner para organizar a agenda, as finanças e muito mais. Baixe o app e, ao criar a sua conta, use o meu código {codigo} para ganhar 10 dias com todas as abas liberadas.', { codigo })
    + '\n\n' + textoLinks(codigo);
  // 0.65: pelo WhatsApp, a mensagem já vai escrita (só escolher o contato)
  if (viaWhats) return semBloqueio(() => Linking.openURL('https://wa.me/?text=' + encodeURIComponent(msg))).catch(() => Share.share({ message: msg }).catch(() => {}));
  return semBloqueio(() => Share.share({ message: msg })).catch(() => {});
}

/** Código de quem indicou, guardado ao abrir o link do convite (preenche sozinho ao criar a conta). */
const CHAVE_IND = 'indicacao_pendente';
export const guardarIndicacao = (c: string) => AsyncStorage.setItem(CHAVE_IND, c.toUpperCase()).catch(() => {});
export const pegarIndicacao = () => AsyncStorage.getItem(CHAVE_IND).catch(() => null);
/** Tira o código de um link de indicação (https://myplanner.expo.app/baixar?i=XXXX ou myplanner://baixar?i=XXXX). */
export function indicacaoDoLink(url: string | null | undefined): string | null {
  if (!url || !/baixar/i.test(url)) return null;
  const m = url.match(/[?&]i=([A-Za-z0-9]{4,10})/);
  return m ? m[1].toUpperCase() : null;
}

export type TipoFeedback = 'problema' | 'erro' | 'sugestao';

/** Envia pelo servidor. Se o servidor ainda não estiver preparado (ou sem internet), abre o e-mail. */
export async function enviarFeedback(tipo: TipoFeedback, texto: string, contato: string): Promise<'ok' | 'email'> {
  const corpo = { tipo, texto: texto.trim().slice(0, 5000), contato: contato.trim().slice(0, 200) || null, versao: versaoApp(), plataforma: Platform.OS, idioma: idioma() };
  if (DEMO) return 'ok';
  if (supabase) {
    const { error } = await supabase.from('feedback').insert(corpo);
    if (!error) return 'ok';
    if (/Limite de mensagens/.test(error.message)) throw new Error(t('Limite de mensagens por hoje atingido. Tente amanhã.'));
  }
  await feedbackPorEmail(tipo, texto);
  return 'email';
}

export function feedbackPorEmail(tipo: TipoFeedback, texto: string) {
  const assunto = `My Planner ${versaoApp()} · ${tipo === 'problema' ? t('Problema') : tipo === 'erro' ? t('Erro') : t('Sugestão')}`;
  const url = `mailto:${EMAIL_SUPORTE}?subject=${encodeURIComponent(assunto)}&body=${encodeURIComponent(`${texto}\n\n(${Platform.OS} · ${idioma()})`)}`;
  return semBloqueio(() => Linking.openURL(url)).catch(() => {});
}
